import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import WelcomeTour, { resetTour } from '../components/WelcomeTour';
import {
  DashboardHeader,
  MaturityOverview,
  StatsGrid,
  AssessmentTracker,
  QuickActions,
  AssessmentsList,
  ExpertServices,
  UpgradeSection,
  RecentActivities,
  WelcomeModal,
  AssessmentRequestModal,
} from '../components/dashboard';
import API_URL from '../lib/apiUrl';

const PRODUCT_OWNER_EMAILS = [
  'shri@aivora-techlabs.com',
  'pramod.k@aivora-techlabs.com',
  'shubhang.d@aivora-techlabs.com',
];

const Dashboard = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [activities, setActivities] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [switchingPack, setSwitchingPack] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showAssessmentRequestModal, setShowAssessmentRequestModal] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const isAdminUser = ['super_admin', 'admin', 'org_admin'].includes(user?.role);
  const isProductOwner = PRODUCT_OWNER_EMAILS.includes((user?.email || '').toLowerCase());

  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    setLoadError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, subRes, activitiesRes, draftsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/api/subscription/status`, { headers }),
        axios.get(`${API_URL}/api/dashboard/my-activities`, { headers }).catch(() => ({ data: { activities: [] } })),
        axios.get(`${API_URL}/api/drafts`, { headers }).catch(() => ({ data: { drafts: [] } })),
        axios.get(`${API_URL}/api/dashboard/assessment-summary`, { headers }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setSubscription(subRes.data);
      setActivities(activitiesRes.data.activities || []);
      setDrafts(draftsRes.data.drafts || []);
      setAssessmentDetails(summaryRes.data);
      if (subRes.data?.plan === 'none' && subRes.data?.status === 'inactive') navigate('/choose-plan');
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please sign in again.');
        logout();
      } else {
        setLoadError('Dashboard data could not be loaded. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, navigate, logout]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const welcomeShown = localStorage.getItem('welcome_modal_shown');
    if (!welcomeShown && !loading && stats) setTimeout(() => setShowWelcomeModal(true), 1000);
  }, [loading, stats]);

  const handleRefresh = () => { setRefreshing(true); fetchDashboardData(); };
  const handleTour = () => { resetTour(); setRunTour(true); };
  const closeWelcomeModal = () => {
    setShowWelcomeModal(false);
    localStorage.setItem('welcome_modal_shown', 'true');
    toast.success('Your dashboard is ready.');
  };
  const handleSwitchEdition = async () => {
    if (!isProductOwner) return;
    const newPlan = subscription?.plan === 'pro' ? 'standard' : 'pro';
    setSwitchingPack(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscription/switch-pack`, { plan: newPlan }, { headers: { Authorization: `Bearer ${token}` } });
      setSubscription((current) => ({ ...(current || {}), ...data.subscription }));
      setStats(null);
      setDrafts([]);
      setActivities([]);
      setAssessmentDetails(null);
      await refreshUser();
      toast.success(data.restored_existing_workspace
        ? `Switched to your saved ${data.plan_name} workspace.`
        : `Switched to a fresh ${data.plan_name} workspace.`);
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not switch editions.');
    } finally {
      setSwitchingPack(false);
    }
  };

  const overallScore = assessmentDetails?.overall_score || 0;
  const progressPercentage = assessmentDetails?.progress_percentage || 0;
  const stagesCompleted = assessmentDetails?.stages_completed || 0;
  const statusCounts = assessmentDetails?.status_counts || { good: 0, attention: 0, critical: 0 };
  const scoreMessage = stagesCompleted === 0
    ? 'Start your ISO 27001 maturity assessment to establish a readiness baseline.'
    : progressPercentage < 100
      ? `Your assessment is ${progressPercentage}% complete. Continue the remaining stages to strengthen the maturity signal.`
      : overallScore >= 80
        ? 'Strong security maturity. Review the remaining findings before audit preparation.'
        : overallScore >= 50
          ? 'Good progress. Prioritize categories that still need attention.'
          : 'Your baseline is complete. Focus on critical gaps first.';

  if (loading) {
    return (
      <div className="dashboard-shell flex min-h-screen items-center justify-center" data-testid="dashboard-loading-state">
        <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl border border-emerald-400/20 bg-emerald-400/10"><ShieldCheck className="h-6 w-6 animate-pulse text-emerald-300" /></div>
          <p className="mt-5 text-sm text-slate-400">Preparing your compliance workspace...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="dashboard-shell min-h-screen overflow-x-hidden pb-10 text-slate-100" data-testid="dashboard-page">
      <WelcomeModal show={showWelcomeModal} onClose={closeWelcomeModal} />
      <AssessmentRequestModal show={showAssessmentRequestModal} onClose={() => setShowAssessmentRequestModal(false)} />
      <WelcomeTour run={runTour} onComplete={() => setRunTour(false)} isAdmin={isAdminUser} />

      <DashboardHeader
        user={user}
        subscription={subscription}
        isAdminUser={isAdminUser}
        isProductOwner={isProductOwner}
        switchingPack={switchingPack}
        refreshing={refreshing}
        onSwitchEdition={handleSwitchEdition}
        onRefresh={handleRefresh}
        onTour={handleTour}
        onLogout={logout}
      />

      <main className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        {loadError && (
          <div className="mb-6 flex flex-col justify-between gap-4 rounded-xl border border-red-400/25 bg-red-400/10 p-4 sm:flex-row sm:items-center" data-testid="dashboard-load-error">
            <div className="flex items-start gap-3 text-sm text-red-100"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" /><span>{loadError}</span></div>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="border-red-300/25 bg-red-300/10 text-red-100 hover:bg-red-300/20 hover:text-white" data-testid="dashboard-load-retry-button"><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
          </div>
        )}

        <MaturityOverview
          overallScore={overallScore}
          progressPercentage={progressPercentage}
          stagesCompleted={stagesCompleted}
          statusCounts={statusCounts}
          scoreMessage={scoreMessage}
        />

        <StatsGrid stats={stats} subscription={subscription} />

        {subscription && stats && (
          <div className="mb-6" data-tour="assessment-tracker">
            <AssessmentTracker used={subscription.assessments_used || 0} total={subscription.assessments_limit || 0} plan={subscription.plan} />
          </div>
        )}

        <QuickActions stats={stats} subscription={subscription} assessmentDetails={assessmentDetails} isAdminUser={isAdminUser} navigate={navigate} />
        <AssessmentsList stats={stats} drafts={drafts} navigate={navigate} />
        <RecentActivities activities={activities} isAdmin={isAdminUser} navigate={navigate} />
        <ExpertServices user={user} navigate={navigate} />
        <UpgradeSection subscription={subscription} onRequestMore={() => setShowAssessmentRequestModal(true)} />
      </main>

      <AnimatePresence>
        {showScrollTop && (
          <motion.button initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-slate-900/90 text-white shadow-xl backdrop-blur-xl transition-colors hover:bg-emerald-500 hover:text-slate-950" data-testid="dashboard-scroll-top-button" title="Scroll to top"><ArrowUp className="h-4 w-4" /></motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;